import * as THREE from 'three/webgpu';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { catalog, categories } from './catalog.js';
import { ChemicalExperiment } from './chemicalExperiment.js';
import { RobotArmIK } from './robotArmIK.js';
import './styles.css';

const canvas = document.querySelector('#scene-canvas');
const loadingScreen = document.querySelector('#loading-screen');
const loadingTitle = document.querySelector('#loading-title');
const progressBar = document.querySelector('#progress-bar');
const progressLabel = document.querySelector('#progress-label');
const runtimeLabel = document.querySelector('#runtime-label');
const catalogList = document.querySelector('#catalog-list');
const searchInput = document.querySelector('#asset-search');
const catalogPanel = document.querySelector('#catalog-panel');
const mobileScrim = document.querySelector('#mobile-scrim');
const titleElement = document.querySelector('#asset-title');
const categoryElement = document.querySelector('#asset-category');
const descriptionElement = document.querySelector('#asset-description');
const metricObjects = document.querySelector('#metric-objects');
const metricTriangles = document.querySelector('#metric-triangles');
const metricMaterials = document.querySelector('#metric-materials');
const footerObjectCount = document.querySelector('#footer-object-count');
const footerMaterialCount = document.querySelector('#footer-material-count');
const toast = document.querySelector('#toast');
const experimentPanel = document.querySelector('#experiment-panel');
const experimentPhase = document.querySelector('#experiment-phase');
const experimentDetail = document.querySelector('#experiment-detail');
const experimentTime = document.querySelector('#experiment-time');
const experimentProgressBar = document.querySelector('#experiment-progress-bar');
const experimentPlayButton = document.querySelector('#experiment-play');
const ikButton = document.querySelector('#ik-button');

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x080b10);
scene.fog = new THREE.FogExp2(0x080b10, 0.014);

const camera = new THREE.PerspectiveCamera(42, window.innerWidth / window.innerHeight, 0.002, 150);
camera.position.set(5, 4, 5);

const renderer = new THREE.WebGPURenderer({
  canvas,
  antialias: true,
  alpha: false,
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.2;
await renderer.init();

runtimeLabel.textContent = navigator.gpu ? 'WebGPU ACTIVE' : 'WEBGL 2 FALLBACK';
document.body.dataset.renderer = navigator.gpu ? 'webgpu' : 'webgl';

const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.065;
controls.rotateSpeed = 0.58;
controls.panSpeed = 0.7;
controls.zoomSpeed = 0.72;
controls.minDistance = 0.02;
controls.maxDistance = 80;
controls.autoRotateSpeed = 0.72;

scene.add(new THREE.HemisphereLight(0xdceeff, 0x16202e, 2.25));

const keyLight = new THREE.DirectionalLight(0xffffff, 4.8);
keyLight.position.set(5, 8, 6);
scene.add(keyLight);

const fillLight = new THREE.DirectionalLight(0x7ec8ff, 2.4);
fillLight.position.set(-6, 3, 2);
scene.add(fillLight);

const rimLight = new THREE.DirectionalLight(0xffc98a, 1.6);
rimLight.position.set(2, 4, -6);
scene.add(rimLight);

const grid = new THREE.GridHelper(10, 28, 0x335b75, 0x162631);
grid.material.opacity = 0.25;
grid.material.transparent = true;
grid.material.depthWrite = false;
scene.add(grid);

const stageMaterial = new THREE.MeshStandardMaterial({
  color: 0x101820,
  roughness: 0.92,
  metalness: 0.08,
  transparent: true,
  opacity: 0.82,
  depthWrite: false,
});
const stage = new THREE.Mesh(new THREE.CircleGeometry(5, 96), stageMaterial);
stage.rotation.x = -Math.PI / 2;
stage.renderOrder = -2;
scene.add(stage);

const focusRingMaterial = new THREE.MeshBasicMaterial({
  color: 0x4cc9ff,
  transparent: true,
  opacity: 0.38,
  side: THREE.DoubleSide,
  depthWrite: false,
});
const focusRing = new THREE.Mesh(new THREE.RingGeometry(0.95, 1, 96), focusRingMaterial);
focusRing.rotation.x = -Math.PI / 2;
focusRing.visible = false;
scene.add(focusRing);

const materialCache = new Map();
let modelRoot;
let modelMeshes = [];
let currentEntry = catalog.find((entry) => entry.id === 'full-scene') ?? catalog[0];
let currentSelection = [];
let currentBounds = new THREE.Box3();
let wireframeEnabled = false;
let toastTimer;
let robotArmIK;
let jakaRobotMeshes = [];
let jakaRobotRoot;
const externalDevices = new Map();
let jakaPatrolCurve;
let jakaPatrolGroundY = 0;
let jakaPatrolScale = 1;
let jakaPatrolling = false;
let uplcDoorAnimation;
let chemicalExperiment;
let previousFrameTime = 0;

const JAKA_TARGET_HEIGHT = 1.9;

const deviceDefinitions = [
  { id: 'uplc', file: 'uplc_web.glb', targetHeight: 1.32 },
];

class HingedDoorAnimation {
  constructor(door) {
    this.door = door;
    this.openQuaternion = door.quaternion.clone();
    this.closedQuaternion = new THREE.Quaternion()
      .setFromAxisAngle(new THREE.Vector3(0, 1, 0), THREE.MathUtils.degToRad(150))
      .multiply(this.openQuaternion);
    this.restart();
  }

  close() {
    this.door.quaternion.copy(this.closedQuaternion);
  }

  restart() {
    this.startedAt = performance.now() * 0.001;
    this.close();
  }

  update(time) {
    const phase = (time - this.startedAt) % 10;
    let openness = 0;

    if (phase >= 3 && phase < 4.2) {
      openness = (phase - 3) / 1.2;
    } else if (phase >= 4.2 && phase < 6.8) {
      openness = 1;
    } else if (phase >= 6.8 && phase < 8) {
      openness = 1 - (phase - 6.8) / 1.2;
    }

    const easedOpenness = openness * openness * (3 - 2 * openness);
    this.door.quaternion.slerpQuaternions(
      this.closedQuaternion,
      this.openQuaternion,
      easedOpenness,
    );
  }
}

function createPresentationMaterial(source) {
  const name = source.name.toLowerCase();
  const color = source.color?.clone() ?? new THREE.Color(0x9ca8b2);
  let material;

  if (name.includes('glass')) {
    material = new THREE.MeshPhysicalMaterial({
      name: source.name,
      color: new THREE.Color(0x7fc9e8),
      roughness: 0.12,
      metalness: 0.04,
      transmission: 0.68,
      thickness: 0.055,
      ior: 1.45,
      transparent: true,
      opacity: 0.56,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  } else if (name.includes('metal')) {
    material = new THREE.MeshStandardMaterial({
      name: source.name,
      color: new THREE.Color(0x7f8d98),
      roughness: 0.24,
      metalness: 0.92,
    });
  } else {
    material = new THREE.MeshStandardMaterial({
      name: source.name,
      color,
      map: source.map ?? null,
      roughness: source.roughness ?? 0.55,
      roughnessMap: source.roughnessMap ?? null,
      metalness: source.metalness ?? 0,
      metalnessMap: source.metalnessMap ?? null,
      normalMap: source.normalMap ?? null,
      emissive: source.emissive?.clone() ?? new THREE.Color(0x000000),
      emissiveMap: source.emissiveMap ?? null,
      transparent: source.transparent,
      opacity: source.opacity,
      alphaMap: source.alphaMap ?? null,
    });
  }

  // CAD exports in this project contain mixed face winding, so all imported
  // model materials must render both sides consistently in every catalog view.
  material.side = THREE.DoubleSide;
  material.wireframe = wireframeEnabled;
  material.needsUpdate = true;
  return material;
}

function enhanceModelMaterials(root) {
  root.traverse((object) => {
    if (!object.isMesh) return;

    object.frustumCulled = true;
    object.castShadow = false;
    object.receiveShadow = false;

    const sourceMaterials = Array.isArray(object.material) ? object.material : [object.material];
    const materials = sourceMaterials.map((source) => {
      if (!materialCache.has(source.uuid)) {
        materialCache.set(source.uuid, createPresentationMaterial(source));
      }
      return materialCache.get(source.uuid);
    });
    object.material = Array.isArray(object.material) ? materials : materials[0];
  });
}

function formatNumber(value) {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value >= 100_000 ? 0 : 1)}K`;
  return String(value);
}

function meshTriangles(mesh) {
  if (mesh.geometry.index) return mesh.geometry.index.count / 3;
  return mesh.geometry.attributes.position.count / 3;
}

function resolveEntryMeshes(entry) {
  if (entry.id === 'robot-arm' && robotArmIK) return robotArmIK.meshes;
  if (entry.proceduralId === 'chemical-experiment') return chemicalExperiment?.meshes ?? [];
  if (entry.proceduralId === 'jaka-mobile-manipulator') return jakaRobotMeshes;
  if (entry.externalId) return externalDevices.get(entry.externalId)?.meshes ?? [];
  if (entry.all) return modelMeshes;

  let matches = [];
  if (entry.names) {
    matches = modelMeshes.filter((mesh) => entry.names.some(
      (name) => mesh.name === name || mesh.name.startsWith(`${name}__`),
    ));
  } else if (entry.prefix) {
    matches = modelMeshes.filter((mesh) => mesh.name.startsWith(entry.prefix));
  }

  matches.sort((left, right) => {
    if (left.name === entry.prefix) return -1;
    if (right.name === entry.prefix) return 1;
    return left.name.length - right.name.length || left.name.localeCompare(right.name);
  });

  if (!entry.representative || matches.length === 0) return matches;

  const instanceName = (meshName) => {
    const materialSeparator = meshName.lastIndexOf('__');
    return materialSeparator === -1 ? meshName : meshName.slice(0, materialSeparator);
  };
  const representativeName = instanceName(matches[0].name);
  return matches.filter((mesh) => instanceName(mesh.name) === representativeName);
}

function normalizeAndPlaceDevice(asset, targetHeight, groundY, innerEdgeX, centerZ) {
  asset.root.rotation.y = -Math.PI / 2;
  asset.root.updateMatrixWorld(true);
  const sourceBounds = calculateBounds(asset.meshes);
  const sourceHeight = sourceBounds.getSize(new THREE.Vector3()).y;
  asset.root.scale.setScalar(targetHeight / Math.max(sourceHeight, 0.001));
  asset.root.updateMatrixWorld(true);

  const scaledBounds = calculateBounds(asset.meshes);
  const scaledCenter = scaledBounds.getCenter(new THREE.Vector3());
  asset.root.position.add(new THREE.Vector3(
    innerEdgeX - scaledBounds.min.x,
    groundY - scaledBounds.min.y,
    centerZ - scaledCenter.z,
  ));
  asset.root.updateMatrixWorld(true);
}

function arrangeExternalDevices(labBounds) {
  const labCenter = labBounds.getCenter(new THREE.Vector3());
  const groundY = labBounds.min.y;
  const innerEdgeX = labBounds.max.x + 1.5;
  const gap = 0.22;
  const sizes = [];

  for (const definition of deviceDefinitions) {
    const asset = externalDevices.get(definition.id);
    normalizeAndPlaceDevice(asset, definition.targetHeight, groundY, innerEdgeX, 0);
    sizes.push(calculateBounds(asset.meshes).getSize(new THREE.Vector3()).z);
  }

  const rowLength = sizes.reduce((sum, size) => sum + size, 0) + gap * (sizes.length - 1);
  let cursor = labCenter.z - rowLength * 0.5;
  for (let index = 0; index < deviceDefinitions.length; index += 1) {
    const asset = externalDevices.get(deviceDefinitions[index].id);
    const currentBounds = calculateBounds(asset.meshes);
    const targetCenterZ = cursor + sizes[index] * 0.5;
    asset.root.position.z += targetCenterZ - currentBounds.getCenter(new THREE.Vector3()).z;
    asset.root.updateMatrixWorld(true);
    cursor += sizes[index] + gap;
  }

  for (const asset of externalDevices.values()) {
    asset.layoutTransform = {
      position: asset.root.position.clone(),
      quaternion: asset.root.quaternion.clone(),
      scale: asset.root.scale.clone(),
    };
  }

  const rowBounds = new THREE.Box3();
  for (const asset of externalDevices.values()) rowBounds.union(calculateBounds(asset.meshes));
  const aisleCenterX = (labBounds.max.x + rowBounds.min.x) * 0.5;
  const patrolMinZ = Math.min(labBounds.min.z, rowBounds.min.z) + 0.35;
  const patrolMaxZ = Math.max(labBounds.max.z, rowBounds.max.z) - 0.35;
  const loopWidth = Math.max((rowBounds.min.x - labBounds.max.x) * 0.17, 0.12);
  jakaPatrolCurve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(aisleCenterX - loopWidth, groundY, patrolMinZ),
    new THREE.Vector3(aisleCenterX + loopWidth, groundY, patrolMinZ),
    new THREE.Vector3(aisleCenterX + loopWidth, groundY, patrolMaxZ),
    new THREE.Vector3(aisleCenterX - loopWidth, groundY, patrolMaxZ),
  ], true, 'catmullrom', 0.18);

  const jakaBounds = calculateBounds(jakaRobotMeshes);
  const jakaHeight = jakaBounds.getSize(new THREE.Vector3()).y;
  jakaPatrolScale = JAKA_TARGET_HEIGHT / Math.max(jakaHeight, 0.001);
  jakaPatrolGroundY = groundY;
}

function placeJaka(centerX, centerZ, groundY, heading) {
  jakaRobotRoot.position.set(0, 0, 0);
  jakaRobotRoot.rotation.set(0, heading, 0);
  jakaRobotRoot.scale.setScalar(jakaPatrolScale);
  jakaRobotRoot.updateMatrixWorld(true);

  const bounds = calculateBounds(jakaRobotMeshes);
  const center = bounds.getCenter(new THREE.Vector3());
  jakaRobotRoot.position.set(centerX - center.x, groundY - bounds.min.y, centerZ - center.z);
  jakaRobotRoot.updateMatrixWorld(true);
}

function setExternalDevicePresentation(entry) {
  for (const [id, asset] of externalDevices) {
    const layout = asset.layoutTransform;
    asset.root.position.copy(layout.position);
    asset.root.quaternion.copy(layout.quaternion);
    asset.root.scale.copy(layout.scale);
    asset.root.updateMatrixWorld(true);

    if (entry.externalId !== id) continue;
    asset.root.position.set(0, 0, 0);
    asset.root.rotation.set(0, 0, 0);
    asset.root.updateMatrixWorld(true);
    const bounds = calculateBounds(asset.meshes);
    const center = bounds.getCenter(new THREE.Vector3());
    asset.root.position.set(-center.x, -bounds.min.y, -center.z);
    asset.root.updateMatrixWorld(true);
  }
}

function setJakaPresentation(entry) {
  const isolated = entry.id === 'jaka-mobile-manipulator';
  jakaPatrolling = Boolean(entry.all);
  jakaRobotRoot.visible = isolated || jakaPatrolling;

  if (isolated) {
    placeJaka(0, 0, 0, 0.75);
  } else if (jakaPatrolling) {
    updateJakaPatrol(0);
  }
}

function updateExperimentUI() {
  if (!chemicalExperiment) return;
  const phase = chemicalExperiment.phase;
  experimentPhase.textContent = phase.label;
  experimentDetail.textContent = phase.detail;
  experimentTime.textContent = `00:${String(Math.floor(chemicalExperiment.elapsed)).padStart(2, '0')} / 00:${chemicalExperiment.duration}`;
  experimentProgressBar.style.width = `${chemicalExperiment.elapsed / chemicalExperiment.duration * 100}%`;
  experimentPanel.querySelectorAll('[data-step]').forEach((step) => {
    const index = Number(step.dataset.step);
    step.classList.toggle('is-active', index === Math.min(phase.index, 3));
    step.classList.toggle('is-complete', index < phase.index);
  });
  experimentPlayButton.classList.toggle('is-paused', !chemicalExperiment.playing);
  experimentPlayButton.title = chemicalExperiment.playing ? '暂停实验' : '继续实验';
  experimentPlayButton.ariaLabel = chemicalExperiment.playing ? '暂停实验' : '继续实验';
}

function updateJakaPatrol(time) {
  if (!jakaPatrolCurve || !jakaPatrolling) return;
  const progress = (time * 0.018) % 1;
  const point = jakaPatrolCurve.getPointAt(progress);
  const tangent = jakaPatrolCurve.getTangentAt(progress);
  placeJaka(point.x, point.z, jakaPatrolGroundY, Math.atan2(tangent.x, tangent.z));
}

function calculateBounds(meshes) {
  const bounds = new THREE.Box3();
  for (const mesh of meshes) bounds.expandByObject(mesh, true);
  return bounds;
}

function positionStage(bounds, isolated) {
  const center = bounds.getCenter(new THREE.Vector3());
  const size = bounds.getSize(new THREE.Vector3());
  const radius = Math.max(size.x, size.z, 0.2) * (isolated ? 1.15 : 1.05);
  const groundY = bounds.min.y - Math.max(size.y * 0.015, 0.006);

  grid.position.set(center.x, groundY, center.z);
  grid.scale.setScalar(Math.max(radius / 5, 0.12));
  stage.position.set(center.x, groundY - 0.003, center.z);
  stage.scale.setScalar(Math.max(radius / 5, 0.12));
  focusRing.position.set(center.x, groundY + 0.003, center.z);
  focusRing.scale.setScalar(Math.max(radius * 0.78, 0.06));
  focusRing.visible = isolated;
}

function fitCamera(bounds, animated = true) {
  if (bounds.isEmpty()) return;

  const center = bounds.getCenter(new THREE.Vector3());
  const sphere = bounds.getBoundingSphere(new THREE.Sphere());
  const radius = Math.max(sphere.radius, 0.025);
  const verticalHalfFov = THREE.MathUtils.degToRad(camera.fov * 0.5);
  const horizontalHalfFov = Math.atan(Math.tan(verticalHalfFov) * camera.aspect);
  const halfFov = Math.min(verticalHalfFov, horizontalHalfFov);
  const framingScale = currentEntry.proceduralId === 'chemical-experiment'
    ? 1.32
    : currentEntry.id === 'robot-arm'
    ? 1.48
    : currentEntry.id === 'jaka-mobile-manipulator' ? 1.38 : 1.24;
  const distance = (radius / Math.sin(halfFov)) * framingScale;
  const direction = currentEntry.proceduralId === 'chemical-experiment'
    ? new THREE.Vector3(1.28, 1.12, 1.46).normalize()
    : currentEntry.id === 'jaka-mobile-manipulator'
    ? new THREE.Vector3(1.28, 0.34, 1.2).normalize()
    : new THREE.Vector3(1.28, 0.92, 1.34).normalize();
  const targetLookAt = currentEntry.proceduralId === 'chemical-experiment'
    ? center.clone().add(new THREE.Vector3(0.04, 0.06, 0))
    : currentEntry.id === 'jaka-mobile-manipulator'
    ? center.clone().add(new THREE.Vector3(-0.32, 0.24, 0.32))
    : center;
  const targetPosition = targetLookAt.clone().add(direction.multiplyScalar(distance));

  camera.near = Math.max(radius / 300, 0.001);
  camera.far = Math.max(distance + radius * 18, 20);
  camera.updateProjectionMatrix();

  if (animated) {
    animateCamera(targetPosition, targetLookAt);
  } else {
    camera.position.copy(targetPosition);
    controls.target.copy(targetLookAt);
    controls.update();
  }
}

function animateCamera(targetPosition, targetLookAt) {
  const startPosition = camera.position.clone();
  const startTarget = controls.target.clone();
  const started = performance.now();
  const duration = 620;

  function step(now) {
    const rawProgress = Math.min((now - started) / duration, 1);
    const progress = 1 - (1 - rawProgress) ** 3;
    camera.position.lerpVectors(startPosition, targetPosition, progress);
    controls.target.lerpVectors(startTarget, targetLookAt, progress);
    controls.update();
    if (rawProgress < 1) requestAnimationFrame(step);
  }

  requestAnimationFrame(step);
}

function updateMetrics(meshes) {
  const triangles = meshes.reduce((sum, mesh) => sum + meshTriangles(mesh), 0);
  const materials = new Set();
  for (const mesh of meshes) {
    const meshMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of meshMaterials) materials.add(material.uuid);
  }
  metricObjects.textContent = formatNumber(meshes.length);
  metricTriangles.textContent = formatNumber(Math.round(triangles));
  metricMaterials.textContent = formatNumber(materials.size);
}

function selectEntry(entry, options = {}) {
  if (!modelRoot) return;

  const matches = resolveEntryMeshes(entry);
  if (!matches.length) {
    showToast(`未找到 ${entry.label} 对应的模型节点`);
    return;
  }

  const experimentMode = entry.proceduralId === 'chemical-experiment';
  const robotArmMode = entry.id === 'robot-arm';
  setExternalDevicePresentation(entry);
  for (const mesh of modelMeshes) mesh.visible = entry.all || matches.includes(mesh);
  for (const [id, asset] of externalDevices) {
    asset.root.visible = entry.all || entry.externalId === id;
  }
  chemicalExperiment.setVisible(experimentMode);
  robotArmIK?.setControlMode(experimentMode ? 'external' : 'autonomous');
  robotArmIK?.setVisible(robotArmMode || experimentMode);
  if (robotArmMode) robotArmIK?.setActive(true);
  ikButton.hidden = !robotArmMode;
  ikButton.classList.toggle('is-active', robotArmMode && robotArmIK?.active);
  setJakaPresentation(entry);
  currentEntry = entry;
  if (entry.externalId === 'uplc') {
    uplcDoorAnimation?.restart();
  } else {
    uplcDoorAnimation?.close();
  }
  let displayedMeshes = matches;
  if (entry.all) {
    displayedMeshes = modelMeshes
      .concat(...[...externalDevices.values()].map((asset) => asset.meshes), jakaRobotMeshes);
  } else if (entry.proceduralId === 'chemical-experiment') {
    displayedMeshes = chemicalExperiment.meshes;
    chemicalExperiment.reset();
  }
  currentSelection = displayedMeshes;
  const lightMode = entry.id === 'jaka-mobile-manipulator' || entry.proceduralId === 'chemical-experiment';
  document.body.dataset.assetTheme = lightMode ? 'light' : 'dark';
  scene.background.set(lightMode ? (experimentMode ? 0xb7c3c5 : 0xc5cbce) : 0x080b10);
  scene.fog.color.set(lightMode ? (experimentMode ? 0xb7c3c5 : 0xc5cbce) : 0x080b10);
  stageMaterial.color.set(lightMode ? 0xaab5b7 : 0x101820);
  stageMaterial.opacity = lightMode ? 0.96 : 0.82;
  grid.material.opacity = lightMode ? 0.08 : 0.25;
  keyLight.intensity = lightMode ? 3.5 : 4.8;
  fillLight.intensity = lightMode ? 1.6 : 2.4;
  rimLight.intensity = lightMode ? 1.15 : 1.6;
  renderer.toneMappingExposure = lightMode ? 1.04 : 1.2;
  experimentPanel.hidden = !experimentMode;
  document.body.classList.toggle('is-experiment', experimentMode);
  const layoutBounds = calculateBounds(displayedMeshes);
  currentBounds = experimentMode
    ? calculateBounds(displayedMeshes.filter((mesh) => mesh.name !== 'Desktop'))
    : layoutBounds;
  positionStage(layoutBounds, !entry.all);
  fitCamera(currentBounds, options.animated !== false);
  updateMetrics(displayedMeshes);

  categoryElement.textContent = entry.category;
  titleElement.textContent = entry.label;
  descriptionElement.textContent = entry.description;

  document.querySelectorAll('.asset-button').forEach((button) => {
    button.classList.toggle('is-active', button.dataset.assetId === entry.id);
  });
  document.querySelector('#show-all-button').classList.toggle('is-active', Boolean(entry.all));

  if (window.innerWidth <= 820) closeMobileMenu();
}

function assetIcon(label) {
  const latin = label.match(/[A-Za-z0-9]+/g)?.join('').slice(0, 2).toUpperCase();
  return latin || label.replace(/[^\u4e00-\u9fa5]/g, '').slice(0, 1) || '·';
}

function renderCatalog(filter = '') {
  const normalized = filter.trim().toLowerCase();
  catalogList.replaceChildren();

  for (const category of categories) {
    const entries = catalog.filter((entry) => {
      if (entry.category !== category) return false;
      if (!normalized) return true;
      const searchable = [entry.label, entry.description, ...(entry.names ?? []), entry.prefix ?? '']
        .join(' ')
        .toLowerCase();
      return searchable.includes(normalized);
    });
    if (!entries.length) continue;

    const group = document.createElement('details');
    group.className = 'catalog-group';
    group.open = true;

    const summary = document.createElement('summary');
    summary.innerHTML = `<span>${category}</span><small>${entries.length}</small>`;
    group.append(summary);

    const items = document.createElement('div');
    items.className = 'catalog-items';

    for (const entry of entries) {
      const button = document.createElement('button');
      button.className = `asset-button${entry.id === currentEntry.id ? ' is-active' : ''}`;
      button.dataset.assetId = entry.id;
      button.type = 'button';
      button.innerHTML = `
        <span class="asset-icon">${assetIcon(entry.label)}</span>
        <span class="asset-copy">
          <strong>${entry.label}</strong>
          <small>${entry.count ? `${entry.count} 件 · 单件观察` : entry.featured ? '重点设备' : entry.description}</small>
        </span>
        <span class="asset-arrow">↗</span>
      `;
      button.addEventListener('click', () => selectEntry(entry));
      items.append(button);
    }

    group.append(items);
    catalogList.append(group);
  }

  if (!catalogList.children.length) {
    catalogList.innerHTML = '<div class="empty-state">没有匹配的设备或素材</div>';
  }
}

function showToast(message) {
  clearTimeout(toastTimer);
  toast.textContent = message;
  toast.classList.add('is-visible');
  toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2600);
}

function openMobileMenu() {
  catalogPanel.classList.add('is-open');
  mobileScrim.classList.add('is-visible');
}

function closeMobileMenu() {
  catalogPanel.classList.remove('is-open');
  mobileScrim.classList.remove('is-visible');
}

renderCatalog();

searchInput.addEventListener('input', (event) => renderCatalog(event.target.value));
document.addEventListener('keydown', (event) => {
  if (event.key === '/' && document.activeElement !== searchInput) {
    event.preventDefault();
    searchInput.focus();
  }
  if (event.key === 'Escape') {
    searchInput.blur();
    closeMobileMenu();
  }
});

document.querySelector('#menu-toggle').addEventListener('click', openMobileMenu);
document.querySelector('#menu-close').addEventListener('click', closeMobileMenu);
mobileScrim.addEventListener('click', closeMobileMenu);
document.querySelector('#show-all-button').addEventListener('click', () => {
  selectEntry(catalog.find((entry) => entry.id === 'full-scene'));
});
document.querySelector('#fit-button').addEventListener('click', () => fitCamera(currentBounds));
document.querySelector('#rotate-button').addEventListener('click', (event) => {
  controls.autoRotate = !controls.autoRotate;
  event.currentTarget.classList.toggle('is-active', controls.autoRotate);
  showToast(controls.autoRotate ? '已开启自动旋转' : '已关闭自动旋转');
});
document.querySelector('#wireframe-button').addEventListener('click', (event) => {
  wireframeEnabled = !wireframeEnabled;
  for (const material of materialCache.values()) {
    material.wireframe = wireframeEnabled;
    material.needsUpdate = true;
  }
  chemicalExperiment?.setWireframe(wireframeEnabled);
  event.currentTarget.classList.toggle('is-active', wireframeEnabled);
  showToast(wireframeEnabled ? '已切换为线框模式' : '已恢复实体材质');
});

ikButton.addEventListener('click', (event) => {
  if (!robotArmIK || currentEntry.id !== 'robot-arm') return;
  robotArmIK.setActive(!robotArmIK.active);
  event.currentTarget.classList.toggle('is-active', robotArmIK.active);
  showToast(robotArmIK.active ? '六轴 IK 动画已启动' : '六轴 IK 动画已暂停');
});

experimentPlayButton.addEventListener('click', () => chemicalExperiment?.toggle());
document.querySelector('#experiment-reset').addEventListener('click', () => chemicalExperiment?.reset());

const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath(`${import.meta.env.BASE_URL}draco/`);

const loader = new GLTFLoader();
loader.setDRACOLoader(dracoLoader);

loader.load(
  `${import.meta.env.BASE_URL}models/labgym_web.glb`,
  async (gltf) => {
    modelRoot = gltf.scene;
    modelRoot.name = 'LabGym_Web_Scene';
    enhanceModelMaterials(modelRoot);
    scene.add(modelRoot);
    modelMeshes = [];
    modelRoot.traverse((object) => {
      if (object.isMesh) modelMeshes.push(object);
    });

    loadingTitle.textContent = '正在载入 ABB、JAKA 与 UPLC';
    progressLabel.textContent = '正在构建六轴 IK、设备区与机器人巡游路径';
    const [abbGltf, jakaGltf, loadedDevices] = await Promise.all([
      loader.loadAsync(`${import.meta.env.BASE_URL}models/abb_ik.glb`),
      loader.loadAsync(`${import.meta.env.BASE_URL}models/jaka_web.glb`),
      Promise.all(deviceDefinitions.map(async (definition) => {
        const deviceGltf = await loader.loadAsync(`${import.meta.env.BASE_URL}models/${definition.file}`);
        const root = deviceGltf.scene;
        root.name = `External_Device_${definition.id.toUpperCase()}`;
        enhanceModelMaterials(root);
        const meshes = [];
        root.traverse((object) => {
          if (object.isMesh) meshes.push(object);
        });
        const asset = { root, meshes };
        if (definition.id === 'uplc') {
          const door = root.getObjectByName('door');
          const axis = root.getObjectByName('axis');
          if (door && axis && door.parent === axis.parent) {
            uplcDoorAnimation = new HingedDoorAnimation(door);
          } else {
            console.warn('UPLC door animation nodes are missing or do not share a parent.');
          }
        }
        externalDevices.set(definition.id, asset);
        scene.add(root);
        return asset;
      })),
    ]);

    enhanceModelMaterials(abbGltf.scene);
    robotArmIK = new RobotArmIK(abbGltf.scene);
    scene.add(robotArmIK.root);

    jakaRobotRoot = jakaGltf.scene;
    jakaRobotRoot.name = 'JAKA_Authored_Mobile_Manipulator';
    enhanceModelMaterials(jakaRobotRoot);
    jakaRobotRoot.visible = false;
    jakaRobotRoot.traverse((object) => {
      if (object.isMesh) jakaRobotMeshes.push(object);
    });
    scene.add(jakaRobotRoot);
    arrangeExternalDevices(calculateBounds(modelMeshes));

    chemicalExperiment = new ChemicalExperiment({ modelMeshes, robotArmIK });
    chemicalExperiment.onStateChange = updateExperimentUI;
    scene.add(chemicalExperiment.root);
    updateExperimentUI();

    const sceneMaterials = new Set();
    const visibleSceneMeshes = modelMeshes
      .concat(...loadedDevices.map((asset) => asset.meshes), jakaRobotMeshes);
    for (const mesh of visibleSceneMeshes) {
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const material of materials) sceneMaterials.add(material.uuid);
    }
    footerObjectCount.textContent = visibleSceneMeshes.length;
    footerMaterialCount.textContent = sceneMaterials.size;

    selectEntry(currentEntry, { animated: false });
    loadingScreen.classList.add('is-complete');
    setTimeout(() => loadingScreen.remove(), 720);
    showToast('场景加载完成 · 点击左侧素材开始浏览');
  },
  (event) => {
    const ratio = event.total ? event.loaded / event.total : 0;
    const percent = Math.min(Math.round(ratio * 100), 99);
    progressBar.style.width = `${Math.max(percent, 3)}%`;
    progressLabel.textContent = event.total
      ? `下载模型 ${percent}% · ${(event.loaded / 1024 / 1024).toFixed(1)} MB`
      : `已下载 ${(event.loaded / 1024 / 1024).toFixed(1)} MB`;
    if (ratio > 0.98) loadingTitle.textContent = '正在解码与构建场景';
  },
  (error) => {
    console.error(error);
    loadingTitle.textContent = '模型加载失败';
    progressLabel.textContent = '请确认 GLB 与 Draco 解码器已部署到 public 目录。';
    progressBar.style.width = '100%';
    progressBar.classList.add('is-error');
  },
);

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  fitCamera(currentBounds, false);
});

renderer.setAnimationLoop((time) => {
  const delta = previousFrameTime ? (time - previousFrameTime) * 0.001 : 0;
  previousFrameTime = time;
  controls.update();
  if (jakaRobotRoot?.visible) {
    updateJakaPatrol(time * 0.001);
  }
  if (currentEntry.externalId === 'uplc') {
    uplcDoorAnimation?.update(time * 0.001);
  }
  chemicalExperiment?.update(delta);
  robotArmIK?.update(time * 0.001, delta);
  if (focusRing.visible) {
    focusRingMaterial.opacity = 0.28 + Math.sin(performance.now() * 0.0018) * 0.1;
  }
  renderer.render(scene, camera);
});
